import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type { Server } from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Confirm,
  EmptyState,
  Notice,
  Skeleton,
} from '../components/ui.tsx';
import { usePermission } from '../components/operational.tsx';
import { SERVER_STATUS, presentation } from '../status.ts';
import { AddServerForm } from './AddServerForm.tsx';
import './Servers.css';

/**
 * The customer's servers, and what we found on them.
 *
 * A server card answers the question a non-technical customer actually has —
 * "is this one usable?" — before any of the detail. Where something on the
 * machine would get in the way, it is named, never removed.
 */

export function Servers({
  startAdding = false,
  onChanged,
}: {
  startAdding?: boolean;
  onChanged?: () => void;
}): ReactNode {
  const { t } = useI18n();
  const [servers, setServers] = useState<readonly Server[] | undefined>();
  const [adding, setAdding] = useState(startAdding);
  const [removing, setRemoving] = useState<Server | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [message, setMessage] = useState<string | undefined>();
  const add = usePermission('server.write');

  const load = async (): Promise<void> => {
    try {
      setServers(await api.servers());
      setError(undefined);
    } catch {
      setError(t('common.somethingWentWrong'));
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await api.servers().catch(() => undefined);
      if (cancelled) return;
      if (list) setServers(list);
      else setError(t('common.somethingWentWrong'));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An inspection takes a little while; poll only while one is in flight, so
  // an idle page makes no requests at all.
  const inspecting = servers?.some((server) => server.status === 'inspecting') ?? false;
  useEffect(() => {
    if (!inspecting) return;
    const timer = setInterval(() => void load(), 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspecting]);

  const remove = async (server: Server): Promise<void> => {
    setRemoving(undefined);
    try {
      await api.removeServer(server.id);
      // The API used to return the sentence to show here, in English. This
      // one is in the customer's language.
      setMessage(t('servers.removed'));
      await load();
      onChanged?.();
    } catch {
      setError(t('common.somethingWentWrong'));
    }
  };

  const recheck = async (server: Server): Promise<void> => {
    try {
      await api.inspectServer(server.id);
      await load();
    } catch {
      setError(t('common.somethingWentWrong'));
    }
  };

  if (adding) {
    return (
      <AddServerForm
        onCancel={() => setAdding(false)}
        onAdded={async () => {
          setAdding(false);
          await load();
          onChanged?.();
        }}
      />
    );
  }

  return (
    <>
      <header className="page-header">
        <h1>{t('servers.title')}</h1>
        <Button disabled={!add.allowed} title={add.reason} onClick={() => setAdding(true)}>
          {t('servers.add')}
        </Button>
      </header>

      {message ? <Notice tone="success">{message}</Notice> : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {removing ? (
        <Confirm
          title={t('servers.remove')}
          confirmLabel={t('servers.remove')}
          onCancel={() => setRemoving(undefined)}
          onConfirm={() => void remove(removing)}
        >
          {/* Named, because "this server" in a list of four is not an answer
              to "which one am I about to lose access to". */}
          <p>
            <strong>{removing.name}</strong>
          </p>
          <p>{t('servers.removeConfirm')}</p>
        </Confirm>
      ) : null}

      {servers === undefined && error ? (
        // Not a skeleton. A spinner that never resolves says "still loading"
        // forever, which is the one thing that is definitely not happening.
        <Card>
          <EmptyState
            title={t('error.network')}
            action={
              <Button variant="secondary" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : servers === undefined ? (
        <Card>
          <Skeleton lines={4} />
        </Card>
      ) : servers.length === 0 ? (
        <Card>
          <EmptyState
            title={t('dashboard.noServers')}
            description={t('dashboard.noServersHelp')}
            action={
              <Button disabled={!add.allowed} title={add.reason} onClick={() => setAdding(true)}>
                {t('dashboard.addFirstServer')}
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="server-list">
          {servers.map((server) => (
            <ServerCard
              key={server.id}
              server={server}
              onRecheck={() => void recheck(server)}
              onRemove={() => setRemoving(server)}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function ServerCard({
  server,
  onRecheck,
  onRemove,
}: {
  server: Server;
  onRecheck?: () => void;
  onRemove?: () => void;
}): ReactNode {
  const { t, pick, formatDateTime } = useI18n();
  const status = presentation(
    SERVER_STATUS,
    server.authorizationRevokedAt ? 'retired' : server.status,
    { label: 'servers.statusDraft', tone: 'neutral' },
  );
  const summary = server.summary;

  return (
    <Card as="article" className="server">
      <CardHeader
        title={
          <span className="server__title">
            {server.name}
            <Badge tone={status.tone}>{t(status.label)}</Badge>
          </span>
        }
        description={
          <span className="ltr-text">
            {server.username}@{server.host}
            {server.sshPort === 22 ? '' : `:${server.sshPort}`}
          </span>
        }
        actions={
          server.authorizationRevokedAt ? null : (
            <>
              {onRecheck ? (
                <Button size="sm" variant="secondary" onClick={onRecheck}>
                  {server.status === 'inspecting' ? t('servers.checking') : t('servers.checkAgain')}
                </Button>
              ) : null}
              {onRemove ? (
                <Button size="sm" variant="danger" onClick={onRemove}>
                  {t('servers.remove')}
                </Button>
              ) : null}
            </>
          )
        }
      />

      {summary ? (
        <>
          <p className="server__headline">{pick(summary.headline)}</p>
          <dl className="server__facts">
            {summary.publicAddress ? (
              <Fact label={t('result.address')} value={summary.publicAddress} mono />
            ) : null}
            {/* Through `t()`, so the figures come back in the reader's own
                digits and the label is a word rather than an abbreviation
                that happens to be English. */}
            <Fact
              label={t('servers.cpuRam')}
              value={t('servers.cpuRamValue', {
                cpu: summary.cpuCount,
                memory: summary.memoryGb,
              })}
            />
            <Fact label={t('servers.location')} value={server.locationLabel ?? '—'} />
            <Fact label={t('servers.lastChecked')} value={formatDateTime(server.lastInventoryAt)} />
          </dl>

          {summary.behindNat ? <Badge tone="info">{t('servers.behindNat')}</Badge> : null}

          {summary.existingSoftware.length > 0 ? (
            <Notice tone="neutral" title={t('servers.existingSoftware')}>
              {/* Named, not touched: what is already on the machine is the
                  customer's, and we say so rather than removing it. */}
              <span className="ltr-text">{summary.existingSoftware.join(', ')}</span>
            </Notice>
          ) : null}
          {summary.rebootRequired ? (
            <Notice tone="warning">{t('servers.rebootPending')}</Notice>
          ) : null}
          {summary.canObtainRoot ? null : (
            <Notice tone="danger">{t('servers.cannotElevate')}</Notice>
          )}
        </>
      ) : server.status === 'inspecting' ? (
        <Skeleton lines={2} />
      ) : server.status === 'unreachable' ? (
        <Notice tone="danger">{t('servers.statusUnreachable')}</Notice>
      ) : null}
    </Card>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  if (!value) return null;
  return (
    <div className="server__fact">
      {label ? <dt>{label}</dt> : null}
      <dd className={mono ? 'mono' : undefined}>{value}</dd>
    </div>
  );
}
